/* eslint-disable camelcase */
import React, { useCallback, useEffect, useState } from 'react';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useGlobals } from 'src/shared/contexts/appContext';
import { User } from 'src/shared/models/user';
import { getUsers } from 'src/shared/services/userService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { EmptyGridCell } from 'src/shared/components/atoms/emptyGridCell';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { UserSysProduct } from './userSysProduct';
import { UserEditModal } from './userEditModal';

type UserDataGridProps = {
  selectionId?: number | null;
  groupName?: string | null;
  pageSize?: number;
  searchString?: string;
  associationId?: string;
  districtId?: string;
  status?: number[];
  loadingCallback: (loading: boolean) => void;
  disableOnClickRow: boolean;
};

export const UserDataGrid = (props: UserDataGridProps) => {
  const {
    selectionId = null,
    groupName = null,
    pageSize = DEFAULT_PAGE_SIZE,
    searchString = undefined,
    associationId = undefined,
    districtId = undefined,
    status = undefined,
    loadingCallback,
    disableOnClickRow,
  } = props;

  const { t } = useTranslation(['user', 'member']);
  const { dateStamp, setDateStamp } = useGlobals();
  const isMounted = useIsMounted();
  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [editUserId, setEditUserId] = useState<number>();
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'firstName', sort: 'asc' }]);
  const [rows, setRows] = useState<User[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: pageSize || DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    setTimeout(() => {
      scrollIntoView();
    }, 200);
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    loadingCallback(true);

    try {
      const result = await getUsers(data.page, data.pageSize, sortModel[0].field, sortModel[0].sort, searchString);

      if (isMounted()) {
        const userResult = result.data.results.map((x) => {
          return { ...x, id: x.user_id };
        }); // extendObjectWithId(result.data.results, 'member_id');
        ConsoleHelper.log(userResult, dateStamp);
        updateData('totalRows', result.data.totalItems);
        setTimeout(() => {
          setRows(userResult);
        }, 100);
      }
    } catch (error) {
      if (isMounted()) {
        updateData('loading', false);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [loadingCallback, data.page, data.pageSize, sortModel, searchString, isMounted, dateStamp]);

  useEffect(() => {
    fetchData();
  }, [data.page, data.pageSize, fetchData]);

  const onRowClick = (params) => {
    setEditUserId(params.row.user_id);
  };

  const handleOnSave = () => {
    setDateStamp(new Date());
    setEditUserId(undefined);
  };

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: t('member:tableHeaders.firstName'),
      sortable: true,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.firstName || ''}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('member:tableHeaders.lastName'),
      sortable: true,
      flex: 1.25,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.lastName || ''}</>;
      },
    },
    {
      field: 'email',
      headerName: t('member:tableHeaders.email'),
      sortable: true,
      flex: 1.25,
      renderCell: (params: GridCellParams) => {
        const { email } = params.row;
        return (
          <>
            {email && (
              <Link to={`mailto: ${email}`} style={{ display: 'flex' }}>
                {email}
              </Link>
            )}
            {!email && <EmptyGridCell />}
          </>
        );
      },
    },
    {
      field: 'sysProducts',
      headerName: t('member:tableHeaders.sysProducts'),
      sortable: false,
      flex: 2.5,
      renderCell: (params: GridCellParams) => {
        const sysProds = params.row.sysProducts && params.row.sysProducts !== null ? params.row.sysProducts.map((x) => x.name) : [];
        return (
          <Box sx={{ lineHeight: 'normal', display: 'flex', flexWrap: 'wrap', mb: -0.5, p: 0.75 }}>
            {sysProds.map((x) => (
              <UserSysProduct value={x} key={x} />
            ))}
          </Box>
        );
      },
    },
  ];

  return (
    <Box
      ref={scrollRef}
      sx={{
        '& .MuiDataGrid-viewport, & .MuiDataGrid-row, & .MuiDataGrid-renderingZone': {
          maxHeight: 'fit-content !important',
        },
      }}
    >
      <DataGrid<User>
        autoHeight
        columns={columns}
        rows={rows}
        dataModel={data}
        disableSelectionOnClick
        sortModel={sortModel}
        checkboxSelection={false}
        onSelectionModelChange={() => ConsoleHelper.log()}
        selectionModel={[]}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
        onRowClick={!disableOnClickRow ? onRowClick : undefined}
        disablePointer={disableOnClickRow}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
      {editUserId && (
        <UserEditModal userId={editUserId} onClose={() => setEditUserId(undefined)} onSave={() => handleOnSave()} modalOpen={!!editUserId} />
      )}
    </Box>
  );
};
